import { ApiDefinition, ApiRequest, ApiResponse, callApi } from "@/api/call-api";
import { apiQueryKey } from "@/api/query-client";
import { useApiSession } from "@Context/common/general/api-session-context";
import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";

type Options<T extends ApiDefinition, TData> = Omit<UseQueryOptions<ApiResponse<T>, Error, TData>, "queryKey" | "queryFn"> & {
  api: T;
  request: ApiRequest<T>;
  /** Additional scope, such as the account ID. Authentication is included automatically. */
  queryKey: QueryKey;
  disableLoader?: boolean;
  backgroundLoader?: boolean;
};

/**
 * Cached, declarative API reads backed by TanStack Query.
 * Runs when enabled and subscribes to shared data; do not call it from an event
 * handler or add a fetching useEffect. Request changes produce a new cache key.
 *
 * @example
 * const leaderboard = useApiQuery({
 *   api: Api.Stats.GetLeaderboard,
 *   request: { score_field: ScoreFieldTypes.BYB_IQ, score_type: ScoreTypes.MAXIMUM, challenge_id: id },
 *   queryKey: [userDocId],
 *   enabled: Boolean(firebaseUid && userDocId && id),
 *   disableLoader: true,
 * });
 * // leaderboard.data is the endpoint response (an array here, NOT data.data).
 * // Render isPending/isLoading, error, and data; call refetch() for an explicit retry.
 * // For writes use useApiMutation. callApi() is the plain Firebase transport.
 */
export default function useApiQuery<T extends ApiDefinition, TData = ApiResponse<T>>({
  api,
  request,
  queryKey,
  disableLoader = false,
  backgroundLoader = false,
  ...options
}: Options<T, TData>) {
  const firebaseUid = useApiSession();
  return useQuery({
    ...options,
    queryKey: apiQueryKey(api.Name, firebaseUid, queryKey, request),
    queryFn: () => callApi(api, request),
    meta: { ...options.meta, apiName: api.Name, disableLoader, backgroundLoader },
  });
}
